// Fitted chair sit: seat the avatar ON the cushion with its legs over the
// front edge, sized from the avatar's own legs and the seat marker's box.
//
// The shared sit clip folds the thighs ~45° down. That suits a shallow dining
// chair, but on a deep seat (the conference rooms' 0.77 m tub pods) hips that
// rest on the cushion send both thighs and calves through the seat's front.
// Instead of moving the whole avatar by fixed per-rig constants, a fitted
// sit poses the legs for the seat in front of it:
//   - hips rest on the cushion: the lowest point of the seat of the avatar
//     (measured from its skinned mesh) touches the marker's top;
//   - thighs lie level along the cushion, slid forward just far enough that
//     the calves hang clear of the seat's front (marker box front face);
//   - calves hang down from the knee, swung forward only if they would
//     otherwise go through the seat front (short legs on a deep seat) or the
//     feet through the floor (long legs on a low seat).
// Seats opt in with `seatFit: 'deep'` (game-table chairs keep the plain
// fitted height: their 0.46 m markers don't span the chair, and level
// thighs would push the seat of the avatar into the backrest).
// The marker box is the seat: its top is the cushion, its front face the
// furthest-forward point of the chair below the cushion (lip, base).
//
// Remote copies of an avatar run the same fit against the same marker, so
// everyone — including people watching a VR player sit — sees the same pose.

import * as THREE from 'three';
import { humanoidKey } from './rig';

/** Gap kept between the calves and the seat's front edge (m). */
export const CHAIR_LEG_CLEARANCE = 0.02;
/**
 * The seat of the avatar sits this far above the cushion top: resting on it
 * to the eye, without the sit clip's slight sway dipping it into the cushion.
 */
export const CHAIR_SEAT_GAP = 0.01;

/** A seat in its own frame: (cx, cz) the marker centre, fwd its facing. */
export interface ChairSeat {
  cx: number; cz: number;
  /** Facing (world yaw of the marker's +Z). */
  yaw: number;
  /** Cushion top (world y). */
  top: number;
  /** Seat front / back faces as signed distances from the centre along the facing. */
  front: number; back: number;
}

/** The avatar's leg measurements (world metres, as scaled in the scene). */
export interface LegDims {
  thigh: number;
  calf: number;
  /** Hip joint height above the underside of the seat (butt / thighs). */
  hipDepth: number;
  /** How far the back of the calf sits behind the knee–ankle line. */
  calfBack: number;
  /** Ankle joint height above the sole. */
  ankle: number;
}

export interface ChairSitSolution {
  /** Hip joint (midpoint of the two thigh joints), relative to the seat centre. */
  hipFwd: number;
  hipY: number;
  /** Calf swing forward of straight down (radians). */
  calfPitch: number;
}

/**
 * Where the hips go and how the calves hang. `floor` (world y) keeps the feet
 * out of the floor; null when unknown.
 */
export function solveChairSit(d: LegDims, seat: Pick<ChairSeat, 'top' | 'front' | 'back'>, floor: number | null): ChairSitSolution {
  const C = CHAIR_LEG_CLEARANCE;
  const hipY = seat.top + d.hipDepth + CHAIR_SEAT_GAP;
  // Level thighs, knee just far enough out that the calf's back clears the
  // front. Longer thighs sit deeper; never so deep the seat of the avatar
  // goes into the backrest, never so far forward it slides off the front.
  const hipMin = seat.back + d.hipDepth;
  const hipMax = Math.max(hipMin, seat.front - d.hipDepth);
  const hipFwd = Math.min(hipMax, Math.max(hipMin, seat.front + d.calfBack + C - d.thigh));
  const kneeFwd = hipFwd + d.thigh;
  // Calves hang straight down when the knee is out past the front. If it
  // isn't (legs too short for the seat), swing them forward until the back of
  // the calf (a cylinder calfBack round the knee–ankle line) passes the seat's
  // front-top corner.
  const a = seat.front + C - kneeFwd, h = hipY - seat.top;
  const dist = Math.hypot(a, h);
  let pitch = dist <= d.calfBack ? Math.PI / 2 : Math.max(0, Math.atan2(a, h) + Math.asin(d.calfBack / dist));
  // Feet above the floor: swing the calves forward as much as that needs.
  if (floor != null && d.calf > 0) {
    const reach = (hipY - floor - d.ankle) / d.calf;
    if (reach < 1) pitch = Math.max(pitch, Math.acos(Math.max(-1, reach)));
  }
  return { hipFwd, hipY, calfPitch: Math.min(pitch, (85 * Math.PI) / 180) };
}

const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();

/** The seat a fitted chair marker describes, from its box (world space). */
export function chairSeatFromMarker(marker: THREE.Object3D): ChairSeat | null {
  marker.updateWorldMatrix(true, false);
  const geo = (marker as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
  if (!geo?.attributes?.position) return null;
  if (!geo.boundingBox) geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  const c = marker.getWorldPosition(new THREE.Vector3());
  const fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(marker.getWorldQuaternion(_q));
  fwd.y = 0;
  if (fwd.lengthSq() < 1e-6) return null;
  fwd.normalize();
  let top = -Infinity, front = -Infinity, back = Infinity;
  for (let i = 0; i < 8; i++) {
    _v.set(i & 1 ? bb.max.x : bb.min.x, i & 2 ? bb.max.y : bb.min.y, i & 4 ? bb.max.z : bb.min.z).applyMatrix4(marker.matrixWorld);
    top = Math.max(top, _v.y);
    const along = (_v.x - c.x) * fwd.x + (_v.z - c.z) * fwd.z;
    front = Math.max(front, along);
    back = Math.min(back, along);
  }
  return { cx: c.x, cz: c.z, yaw: Math.atan2(fwd.x, fwd.z), top, front, back };
}

/**
 * The deep-seat marker an avatar at (x, y, z) is sitting on, if any: the
 * nearest `seatFit: 'deep'` marker within reach, at a height a seated avatar
 * could be on (its root can be well below the cushion).
 */
export function findChairMarker(scene: THREE.Object3D, x: number, y: number, z: number, maxDist = 0.75): THREE.Object3D | null {
  let best: THREE.Object3D | null = null;
  let bestD = maxDist;
  scene.traverse((o) => {
    if (!o.userData?.seat || o.userData.seatFit !== 'deep') return;
    o.getWorldPosition(_v);
    if (_v.y < y - 0.5 || _v.y > y + 1.6) return;
    const d = Math.hypot(_v.x - x, _v.z - z);
    if (d < bestD) { bestD = d; best = o; }
  });
  return best;
}

type Leg = { thigh: THREE.Bone; calf: THREE.Bone; foot: THREE.Bone };

/** Left/right thigh, calf and foot bones of a humanoid rig (Mixamo, Meshy, Tripo, VRM). */
export function findLegBones(root: THREE.Object3D): { left: Leg; right: Leg } | null {
  const b: Record<string, THREE.Bone> = {};
  root.traverse((o) => {
    if (!(o as THREE.Bone).isBone) return;
    const k = humanoidKey(o.name);
    if (k && !b[k]) b[k] = o as THREE.Bone;
  });
  const leg = (s: 'left' | 'right'): Leg | null => {
    const thigh = b[`${s}UpperLeg`], calf = b[`${s}LowerLeg`], foot = b[`${s}Foot`];
    return thigh && calf && foot ? { thigh, calf, foot } : null;
  };
  const left = leg('left'), right = leg('right');
  return left && right ? { left, right } : null;
}

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _c = new THREE.Vector3();
const _rot = new THREE.Quaternion();
const _wq = new THREE.Quaternion();
const _pq = new THREE.Quaternion();
const _cur = new THREE.Vector3();
const _want = new THREE.Vector3();

/** Turn a bone (world space) so its child's direction `cur` becomes `want`. */
function aimBone(bone: THREE.Object3D, cur: THREE.Vector3, want: THREE.Vector3) {
  if (cur.lengthSq() < 1e-10 || want.lengthSq() < 1e-10) return;
  _rot.setFromUnitVectors(_cur.copy(cur).normalize(), _want.copy(want).normalize());
  bone.getWorldQuaternion(_wq);
  _wq.premultiply(_rot);
  if (bone.parent) bone.parent.getWorldQuaternion(_pq).invert(); else _pq.identity();
  bone.quaternion.copy(_pq.multiply(_wq));
  bone.updateMatrixWorld(true);
}

/**
 * Applies the fitted chair sit to one avatar, frame by frame. `body` is the
 * object moved to put the hips on the seat (the avatar's wrapper, a child of
 * the root group); its offset is taken back out with `release()` before
 * anything else (grounding) reads the body's position.
 */
export class ChairSitFitter {
  private legs: { left: Leg; right: Leg } | null;
  private dims: LegDims | null = null;
  private applied = new THREE.Vector3();
  private measured = false;

  constructor(private body: THREE.Object3D) {
    this.legs = findLegBones(body);
  }

  get usable() { return !!this.legs; }
  /** The leg measurements in use (null until the first settled sit). */
  get legDims(): LegDims | null { return this.dims; }

  /** Undo last frame's hip offset (call before the body is re-grounded). */
  release() {
    if (this.applied.lengthSq() === 0) return;
    this.body.position.sub(this.applied);
    this.applied.set(0, 0, 0);
  }

  /**
   * Pose the legs and place the hips for this seat. Call after the clip has
   * posed the body; `settled` once the sit clip is fully blended in (the body
   * is measured then, not mid-crossfade from standing).
   */
  apply(seat: ChairSeat, floor: number | null, settled = true) {
    const legs = this.legs;
    if (!legs) return;
    const parent = this.body.parent;
    if (!parent) return;
    parent.updateWorldMatrix(true, false);
    this.body.updateMatrixWorld(true);
    const fwd = new THREE.Vector3(Math.sin(seat.yaw), 0, Math.cos(seat.yaw));
    const thigh = legs.left.thigh.getWorldPosition(_a).distanceTo(legs.left.calf.getWorldPosition(_b));
    const calf = legs.left.calf.getWorldPosition(_a).distanceTo(legs.left.foot.getWorldPosition(_b));
    // Until measured from the mesh, rough human proportions.
    let d: LegDims = this.dims ?? { thigh, calf, hipDepth: 0.3 * thigh, calfBack: 0.15 * calf, ankle: 0.18 * calf };
    d = { ...d, thigh, calf };
    this.pose(seat, fwd, solveChairSit(d, seat, floor));
    if (!this.measured && settled) {
      // First fitted frame for this body: measure how thick the seat of the
      // avatar and its calves are, and how high its ankles sit, then re-pose.
      this.measured = true;
      const m = this.measure(fwd);
      if (m) {
        this.dims = { thigh, calf, ...m };
        this.pose(seat, fwd, solveChairSit(this.dims, seat, floor));
      }
    }
  }

  private pose(seat: ChairSeat, fwd: THREE.Vector3, s: ChairSitSolution) {
    const legs = this.legs!;
    // 1. Hips onto the seat: shift the body so the thigh joints' midpoint
    //    lands at the solved spot (offset in the parent's frame).
    const hip = legs.left.thigh.getWorldPosition(_a).add(legs.right.thigh.getWorldPosition(_b)).multiplyScalar(0.5);
    const want = _b.set(seat.cx + fwd.x * s.hipFwd, s.hipY, seat.cz + fwd.z * s.hipFwd);
    const parent = this.body.parent!;
    const delta = parent.worldToLocal(want.clone()).sub(parent.worldToLocal(hip.clone()));
    this.body.position.add(delta);
    this.applied.add(delta);
    this.body.updateMatrixWorld(true);
    // 2. Legs: thighs level and straight ahead, calves hanging at the pitch.
    const down = Math.cos(s.calfPitch), ahead = Math.sin(s.calfPitch);
    for (const leg of [legs.left, legs.right]) {
      const A = leg.thigh.getWorldPosition(new THREE.Vector3());
      const K = leg.calf.getWorldPosition(new THREE.Vector3());
      aimBone(leg.thigh, K.sub(A), fwd);
      const K2 = leg.calf.getWorldPosition(new THREE.Vector3());
      const F = leg.foot.getWorldPosition(new THREE.Vector3());
      aimBone(leg.calf, F.sub(K2), new THREE.Vector3(fwd.x * ahead, -down, fwd.z * ahead));
    }
  }

  /** Leg thicknesses from the posed, skinned mesh (relative to the joints). */
  private measure(fwd: THREE.Vector3): Omit<LegDims, 'thigh' | 'calf'> | null {
    const legs = this.legs!;
    const hipY = (legs.left.thigh.getWorldPosition(_a).y + legs.right.thigh.getWorldPosition(_b).y) / 2;
    let seatLow = Infinity, calfBack = 0, soleLow = Infinity;
    const knees = [legs.left, legs.right].map((l) => ({
      K: l.calf.getWorldPosition(new THREE.Vector3()),
      dir: l.foot.getWorldPosition(new THREE.Vector3()).sub(l.calf.getWorldPosition(new THREE.Vector3())),
    }));
    const footY = Math.min(legs.left.foot.getWorldPosition(_a).y, legs.right.foot.getWorldPosition(_b).y);
    const v = new THREE.Vector3();
    let n = 0;
    this.body.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isSkinnedMesh || !m.visible || !m.geometry?.attributes?.skinIndex) return;
      const keys = m.skeleton.bones.map((bone) => {
        for (let p: THREE.Object3D | null = bone; p; p = p.parent) { const k = humanoidKey(p.name); if (k) return k; }
        return null;
      });
      const pos = m.geometry.attributes.position, si = m.geometry.attributes.skinIndex, sw = m.geometry.attributes.skinWeight;
      m.updateMatrixWorld(true);
      for (let i = 0; i < pos.count; i++) {
        let bi = 0, bw = -1;
        for (let k = 0; k < 4; k++) { const w = sw.getComponent(i, k); if (w > bw) { bw = w; bi = si.getComponent(i, k); } }
        const key = keys[bi];
        if (!key) continue;
        const seatPart = key === 'hips' || key === 'leftUpperLeg' || key === 'rightUpperLeg';
        const calfPart = key === 'leftLowerLeg' || key === 'rightLowerLeg';
        const footPart = key === 'leftFoot' || key === 'rightFoot' || key === 'leftToes' || key === 'rightToes';
        if (!seatPart && !calfPart && !footPart) continue;
        v.fromBufferAttribute(pos, i);
        m.applyBoneTransform(i, v);
        v.applyMatrix4(m.matrixWorld);
        n++;
        if (seatPart) { if (v.y < hipY + 0.05) seatLow = Math.min(seatLow, v.y); }
        else if (footPart) soleLow = Math.min(soleLow, v.y);
        else {
          // Distance behind the knee→ankle line, in the leg's own plane.
          const leg = key === 'leftLowerLeg' ? knees[0] : knees[1];
          const len = leg.dir.length();
          if (len < 1e-4) continue;
          const dir = _c.copy(leg.dir).divideScalar(len);
          const rel = _a.copy(v).sub(leg.K);
          const along = rel.dot(dir);
          if (along < 0.2 * len || along > len) continue; // not the bulge behind the knee joint
          const nrm = _b.copy(fwd).addScaledVector(dir, -fwd.dot(dir));
          if (nrm.lengthSq() < 1e-6) continue;
          nrm.normalize();
          calfBack = Math.max(calfBack, -rel.addScaledVector(dir, -along).dot(nrm));
        }
      }
    });
    if (!n || !Number.isFinite(seatLow)) return null;
    return {
      hipDepth: Math.max(0.02, hipY - seatLow),
      calfBack: Math.max(0.01, calfBack),
      ankle: Number.isFinite(soleLow) ? Math.max(0, footY - soleLow) : 0,
    };
  }
}
